import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import "./App.css";
import "./components/CheckinApp.css";
import axios from "axios";

import { Toaster } from 'sonner';
import MaintenanceModal from './components/MaintenanceModal';

// Lazy load components for code splitting
const Home = lazy(() => import('./pages/Home'));
const CustomerDashboard = lazy(() => import('./pages/CustomerDashboard'));
const ReservationLookup = lazy(() => import('./pages/ReservationLookup'));

const InvitePage = lazy(() => import('./pages/InvitePage'));
const OrderConfirmation = lazy(() => import('./pages/OrderConfirmation'));
const AdminDashboard = lazy(() => import('./AdminDashboard'));
const CheckinApp = lazy(() => import('./components/CheckinApp'));
const AdminLogin = lazy(() => import('./components/AuthComponents').then(module => ({ default: module.AdminLogin })));
const CustomerLogin = lazy(() => import('./components/AuthComponents').then(module => ({ default: module.CustomerLogin })));
import { SidebarDemo } from './components/SidebarDemo';

// Loading component
import LoaderOne from './components/ui/loader-one';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import { useTheme } from './context/ThemeContext';

const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-gray-50">
    <LoaderOne />
  </div>
);

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Setup axios interceptors for auth
axios.interceptors.request.use((config) => {
  const customerToken = localStorage.getItem('customer_token');
  const adminToken = localStorage.getItem('admin_token');
  const checkinToken = localStorage.getItem('checkin_token');

  // Bypass LocalTunnel warning
  config.headers['Bypass-Tunnel-Reminder'] = 'true';

  // Prevent caching of API requests
  if (config.url?.includes('/api/')) {
    config.headers['Cache-Control'] = 'no-cache, no-store, must-revalidate';
    config.headers['Pragma'] = 'no-cache';
    config.headers['Expires'] = '0';
  }

  if (adminToken && config.url?.includes('/admin/')) {
    config.headers.Authorization = `Bearer ${adminToken}`;
  } else if (checkinToken && config.url?.includes('/admin/')) {
    config.headers.Authorization = `Bearer ${checkinToken}`;
  } else if (customerToken && config.url?.includes('/customer/')) {
    config.headers.Authorization = `Bearer ${customerToken}`;
  }

  return config;
});

// Response interceptor to handle expired tokens (401 errors)
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Token expired or invalid - clear all tokens and redirect
      const wasAdmin = localStorage.getItem('admin_token') || localStorage.getItem('admin_role');
      const wasCheckin = localStorage.getItem('checkin_token') || localStorage.getItem('checkin_role');
      const wasCustomer = localStorage.getItem('customer_token');

      // Clear all auth data
      localStorage.removeItem('admin_token');
      localStorage.removeItem('admin_role');
      localStorage.removeItem('admin_user');
      localStorage.removeItem('checkin_token');
      localStorage.removeItem('checkin_role');
      localStorage.removeItem('checkin_user');
      localStorage.removeItem('customer_token');

      // Determine redirect path based on what type of user was logged in
      let redirectPath = '/';
      if (wasCheckin) {
        redirectPath = '/checkin';
      } else if (wasAdmin) {
        redirectPath = '/admin/login';
      } else if (wasCustomer) {
        redirectPath = '/customer/login';
      }

      // Only redirect if not already on login page to avoid loops
      const currentPath = window.location.pathname;
      if (!currentPath.includes('/login') && currentPath !== '/checkin') {
        window.location.href = redirectPath;
      } else if (currentPath === '/checkin') {
        // Force reload to show login screen
        window.location.reload();
      }
    }
    return Promise.reject(error);
  }
);

// Protected Route Component
const ProtectedRoute = ({ children, type }) => {
  const token = localStorage.getItem(type === 'admin' ? 'admin_token' : 'customer_token');
  const redirectPath = type === 'admin' ? '/admin/login' : '/customer/login';

  if (!token) {
    return <Navigate to={redirectPath} replace />;
  }

  // Restrict CHECKIN users to the checkin app
  if (type === 'admin') {
    const role = localStorage.getItem('admin_role');
    if (role === 'CHECKIN') {
      return <Navigate to="/checkin" replace />;
    }
  }

  return children;
};

function App() {
  const { seoTitle, seoDescription, seoKeywords, seoImage } = useTheme();

  return (
    <HelmetProvider>
      <Helmet>
        <title>{seoTitle}</title>
        <meta name="description" content={seoDescription} />
        <meta name="keywords" content={seoKeywords} />

        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:title" content={seoTitle} />
        <meta property="og:description" content={seoDescription} />
        <meta property="og:image" content={seoImage} />

        {/* Twitter */}
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:title" content={seoTitle} />
        <meta property="twitter:description" content={seoDescription} />
        <meta property="twitter:image" content={seoImage} />
      </Helmet>
      <Router>
        <MaintenanceModal />
        <Toaster position="top-right" richColors />
        <Suspense fallback={<PageLoader />}>
          <Routes>
            {/* Public Routes */}
            <Route path="/" element={<Home />} />
            <Route path="/reservation-lookup" element={<ReservationLookup />} />

            <Route path="/invite/:reservationId" element={<InvitePage />} />
            <Route path="/order-confirmation" element={<OrderConfirmation />} />

            {/* Auth Routes */}
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/customer/login" element={<CustomerLogin />} />

            {/* Protected Admin Routes */}
            <Route
              path="/admin/dashboard/*"
              element={
                <ProtectedRoute type="admin">
                  <AdminDashboard />
                </ProtectedRoute>
              }
            />

            {/* Protected Customer Routes */}
            <Route
              path="/customer/dashboard"
              element={
                <ProtectedRoute type="customer">
                  <CustomerDashboard />
                </ProtectedRoute>
              }
            />

            {/* Checkin App Route */}
            <Route path="/checkin" element={<CheckinApp />} />

            {/* Sidebar Demo */}
            <Route path="/sidebar-demo" element={<SidebarDemo />} />

            {/* Fallback */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Suspense>
      </Router>
    </HelmetProvider>
  );
}

export default App;